import { useContext, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Card, F0Icon } from '@factorialco/f0-react'
import { Check, Rocket } from '@factorialco/f0-react/icons/app'
import { EnvironmentContext } from 'config/environment'
import {
  CanSeeModulesMarketplaceDocument,
  CanSelfUpsellDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { PricingPlanItemProps } from 'modules/marketplace_modules/components/Revamp/types'
import { useModuleStatus } from 'modules/marketplace_modules/hooks/useModuleStatus'
import { isValidModule } from 'modules/marketplace_modules/lib/typesValidation'
import UpgradePlanModal from 'modules/upsell/components/SelfUpsell/UpgradePlanModal'
import { BUSINESS_VARIANT, ENTERPRISE_VARIANT } from 'modules/upsell/lib/selfUpsell/plans/utils'

export const Item = ({
  addonReference,
  plan,
  onViewAllFeatures,
  onGetItNow,
  onRequestUpsell,
  isAuthorized,
  moduleData,
}: PricingPlanItemProps) => {
  const perksArray = plan.perks ? plan.perks.split('\n').filter(Boolean) : []
  const { data: selfUpsellPolicy } = useQuery(CanSelfUpsellDocument)
  const { data: moduleAuthData } = useQuery(CanSeeModulesMarketplaceDocument)
  const { ignoreUpsell } = useContext(EnvironmentContext)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)

  // Hook that centralizes all queries needed for module status checking
  // Handles subscription items, subscription overloads, and module status map
  const { getModuleStatus, hasEnterpriseVariant } = useModuleStatus(
    addonReference.length > 0 ? [addonReference] : [],
    {}
  )
  const moduleIncludedInPlan =
    moduleData?.includedInPlan && moduleData?.name
      ? [
          {
            moduleName: moduleData.name,
            includedInPlan: moduleData.includedInPlan,
          },
        ]
      : []
  const moduleStatus = getModuleStatus({
    moduleName: moduleData?.name || null,
    billingAddonReference: addonReference.length > 0 ? addonReference : null,
    moduleIncludedInPlan,
  })

  const isInstalled = moduleStatus.isInstalled
  const variant = moduleStatus.variant

  const canSelfUpsell = selfUpsellPolicy?.billing._policies.subscriptionUpgradeSubscription.can
  const canUpsell = moduleAuthData?.modules._policies.moduleManageModule.can

  const onUpgradePlan = () => {
    setIsUpgradeModalOpen(true)
    track(trackingEvents.marketplaceModules.openUpgradeModal, {
      source: 'marketplace_pricing_card_upgrade_modal',
      module: moduleData?.slug,
      origin: window.location.href,
    })
  }

  const handleCloseUpgradeModal = () => {
    setIsUpgradeModalOpen(false)
  }

  const isRecruitment = () => {
    return addonReference?.some((addon) =>
      addon.internalTitle.toLowerCase().includes('recruitment')
    )
  }

  const isExpenses = () => {
    return addonReference?.some((addon) => addon.internalTitle.toLowerCase().includes('expenses'))
  }

  const getPrimaryAction = () => {
    if (!isAuthorized) return undefined

    const showSelfUpsellButton =
      (canSelfUpsell || ignoreUpsell) && !isInstalled && !moduleData?.disableSelfUpsell

    const shouldShowUpgradeButton =
      isInstalled &&
      variant === BUSINESS_VARIANT &&
      canSelfUpsell &&
      hasEnterpriseVariant &&
      !isRecruitment() &&
      !isExpenses() &&
      moduleStatus.from !== 'bundle' &&
      plan.name !== BUSINESS_VARIANT

    const shouldShowRequestUpsellButton =
      (canUpsell && !isInstalled) || moduleData?.disableSelfUpsell

    if (shouldShowUpgradeButton) {
      return {
        label: i18n.t('marketplace.revamp.upgrade_plan'),
        onClick: onUpgradePlan || (() => {}),
      }
    }

    if (showSelfUpsellButton) {
      return {
        label: i18n.t('marketplace.revamp.get_it_now'),
        onClick: onGetItNow || (() => {}),
      }
    }
    if (shouldShowRequestUpsellButton) {
      return {
        label: i18n.t('marketplace.revamp.request_upsell'),
        onClick: onRequestUpsell || (() => {}),
      }
    }

    return undefined
  }

  const shouldShowInYourPlan = isInstalled && plan.name === variant
  return (
    <>
      <F0Card
        avatar={{
          type: 'emoji',
          emoji: plan.icon || '❓',
        }}
        title={
          plan.name === BUSINESS_VARIANT
            ? i18n.t('marketplace.revamp.business')
            : plan.name === ENTERPRISE_VARIANT
            ? i18n.t('marketplace.revamp.enterprise')
            : plan.name || ''
        }
        description={canSelfUpsell && !plan.hasDiscount ? plan.price || '' : ''}
        primaryAction={getPrimaryAction()}
        secondaryActions={[
          {
            label: i18n.t('marketplace.revamp.view_all_features'),
            onClick: onViewAllFeatures || (() => {}),
          },
        ]}
      >
        <Box gap="s8" paddingBottom="s8">
          {canSelfUpsell && plan.hasDiscount && plan.originalPrice && (
            <Box gap="s2">
              <Box flexDirection="row" gap="s2">
                <Text size="200" color="grey700" stroked>
                  {plan.originalPrice}
                </Text>
                <Text size="200" color="grey700">
                  {plan.price}
                </Text>
              </Box>

              <Box flexDirection="row" gap="s2" paddingBottom="s8">
                {plan.seatOriginalPrice && (
                  <Text size="200" color="grey700" stroked>
                    {plan.seatOriginalPrice}
                  </Text>
                )}
                {plan.seatDescription && (
                  <Text size="200" color="grey700">
                    {plan.seatDescription}
                  </Text>
                )}
              </Box>
            </Box>
          )}

          <Box
            flexDirection="row"
            gap="s8"
            alignItems="flexStart"
            paddingY="s8"
            justifyContent="spaceBetween"
          >
            <Box flexDirection="row" gap="s8" alignItems="flexStart">
              <Box flexDirection="row" gap="s8" alignItems="flexStart" paddingTop="s2">
                <F0Icon icon={Rocket} size="md" color="default" />
              </Box>
              <Text size="200" color="black" ellipsis lineClamp={1}>
                {plan.name === BUSINESS_VARIANT
                  ? i18n.t('marketplace.revamp.key_features')
                  : plan.name === ENTERPRISE_VARIANT
                  ? i18n.t('marketplace.revamp.all_features')
                  : plan.title}
              </Text>
            </Box>
            {shouldShowInYourPlan && (
              <Box flexDirection="row" gap="s8" alignItems="flexEnd">
                <F0Icon icon={Check} size="md" color="default" />
                <Text size="200" color="grey700">
                  {i18n.t('marketplace.revamp.in_your_plan')}
                </Text>
              </Box>
            )}
          </Box>
          <Box gap="s16">
            {perksArray.map((perk, index) => (
              <Box key={index} flexDirection="row" gap="s8" alignItems="flexStart">
                {perk.trim() ? (
                  <>
                    <F0Icon icon={Check} size="md" color="default" />
                    <Text size="200" color="black" ellipsis lineClamp={1}>
                      {perk}
                    </Text>
                  </>
                ) : (
                  <Box style={{ height: '20px' }} />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </F0Card>

      {isValidModule(moduleData?.chargebeeModuleName) && (
        <UpgradePlanModal
          installedAddonInternalId={moduleStatus.installedAddonInternalId}
          isOpen={isUpgradeModalOpen}
          onClose={handleCloseUpgradeModal}
          moduleName={moduleData?.chargebeeModuleName}
          from="marketplace_pricing_card"
        />
      )}
    </>
  )
}
